import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import apiService from '../services/api';

interface Props {
  token: string;
  onDone: () => void;
}

const ResetPassword: React.FC<Props> = ({ token, onDone }) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError(t('admin.passwordTooShort', 'A senha deve ter pelo menos 6 caracteres'));
      return;
    }
    if (password !== confirm) {
      setError(t('admin.passwordMismatch', 'As senhas não coincidem'));
      return;
    }
    setLoading(true);
    try {
      const res = await apiService.post('/api/auth/reset-password', { token, password });
      if (res.success) {
        setDone(true);
      } else {
        setError(res.message || t('auth.resetPasswordExpired', 'Link expirado ou inválido'));
      }
    } catch {
      setError(t('auth.resetPasswordExpired', 'Link expirado ou inválido'));
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg, #f5f5f0)',
      padding: 16,
    }}>
      <div style={{
        background: 'var(--surface, #fff)',
        borderRadius: 16,
        padding: '32px 28px',
        maxWidth: 380,
        width: '100%',
        boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: 'color-mix(in srgb, var(--accent, #0b3b2e) 12%, transparent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 12,
          }}>
            <KeyRound size={22} color="var(--accent, #0b3b2e)" />
          </div>
          <h1 style={{ fontSize: 20, fontWeight: 650, color: 'var(--ink, #1a1a1a)', margin: 0 }}>
            {t('auth.resetPassword', 'Redefinir senha')}
          </h1>
        </div>

        {done ? (
          <div style={{ textAlign: 'center' }}>
            <CheckCircle size={40} color="#10b981" style={{ marginBottom: 12 }} />
            <p style={{ fontSize: 14, color: 'var(--ink-muted, #555)', marginBottom: 20 }}>
              {t('auth.resetPasswordSuccess', 'Senha redefinida com sucesso. Você pode fazer login agora.')}
            </p>
            <button className="btn-s primary" style={{ width: '100%', justifyContent: 'center', minHeight: 44 }} onClick={onDone}>
              {t('auth.goToLogin', 'Ir para o login')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {error && (
              <div style={{
                display: 'flex', gap: 8, alignItems: 'flex-start',
                background: 'color-mix(in srgb, var(--danger, #e53e3e) 10%, transparent)',
                border: '1px solid color-mix(in srgb, var(--danger, #e53e3e) 30%, transparent)',
                color: 'var(--danger, #e53e3e)',
                borderRadius: 8, padding: '10px 12px', fontSize: 13,
              }}>
                <XCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                {error}
              </div>
            )}

            <div style={{ position: 'relative' }}>
              <input
                type={showPw ? 'text' : 'password'}
                placeholder={t('admin.newPassword', 'Nova senha')}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoFocus
                style={{ width: '100%', padding: '11px 40px 11px 12px', border: '1px solid var(--line, #ddd)', borderRadius: 8, fontSize: 15, boxSizing: 'border-box' }}
              />
              <button type="button" onClick={() => setShowPw(v => !v)} style={{
                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-faint, #aaa)', padding: 2,
              }}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            <input
              type={showPw ? 'text' : 'password'}
              placeholder={t('admin.confirmPassword', 'Confirmar senha')}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              required
              style={{ padding: '11px 12px', border: '1px solid var(--line, #ddd)', borderRadius: 8, fontSize: 15 }}
            />

            <button type="submit" className="btn-s primary" disabled={loading}
              style={{ justifyContent: 'center', minHeight: 44, marginTop: 4 }}>
              {loading ? t('common.loading', 'Carregando...') : t('auth.resetPassword', 'Redefinir senha')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
